import { Component, OnInit, OnDestroy, inject, signal, Input, EventEmitter, Output, ElementRef, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PillsComponent } from '../shared/pills/pills.component';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { TableCardLayoutComponent } from '../shared/table-card-layout/table-card-layout.component';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Subscription } from 'rxjs/internal/Subscription';
import { Store } from '@ngrx/store';
import { PolicyState } from '../store/policy/policy.reducer';
import { DocumentService } from '../services/document.service';
import { getAuthToken } from '../auth.interceptor';
import { finalize } from 'rxjs/operators';
import { LoadingService } from '../shared/loading.service';
import { ToastService } from '../shared/toast/toast.service';
import { MatDialog } from '@angular/material/dialog';
import { PdfViewerComponent } from '../shared/pdf-viewer/pdf-viewer.component';
import { Renderer2 } from '@angular/core';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { globalMessages } from '../shared/global-messages';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { APP_CONFIG, AppConfig } from 'src/app.config';

/** Categories that route to the Iris task view instead of the PDF viewer */
const IRIS_TASK_CATEGORIES = [
    'third party authentication error',
    'add or modify designated third party',
];

interface Communication {
    direction: 'in' | 'out';
    referenceId: string;
    category: string;
    type: string;
    source: string;
    sendDate: string;
    receivedDate: string;
    status: string;
    attachmentDocId?: string;
    taskId?: string;
}

interface PillData {
    data: {
        name: string;
        selectedItems: number;
        isOpened: boolean;
        selectAll: boolean;
        isFiltered: boolean;
        items: Array<{
            key: string;
            doc_count: number;
            selected: boolean;
            type: string;
        }>;
    };
}

interface ColumnDefinition {
    field: string;
    header: string;
    width: string;
}

@Component({
    selector: 'app-inbound-outbound-comms',
    imports: [CommonModule, ...PSIC_COMPONENTS, FormsModule, PillsComponent, TableCardLayoutComponent, MatIconModule],
    templateUrl: './inbound-outbound-comms.component.html',
    styleUrl: './inbound-outbound-comms.component.scss',
})
export class InboundOutboundCommsComponent implements OnInit, OnDestroy {
    @Input() isCommLoaded = false;
    @Input() commFailed = false;
    @Output() retry = new EventEmitter<void>();
    globalMessages = globalMessages;
    // Using signals for reactive data management
    allCommunications = signal<Communication[]>([]);
    communications = signal<Communication[]>([]);
    filteredCommunications = signal<Communication[]>([]);
    showAll = signal<boolean>(false);
    pills = signal<PillData[]>([]);

    private combinedSubscription: Subscription;
    selectable = false;
    filteredDataFilter: PillData[] = [];
    cols: ColumnDefinition[] = [];
    selectedRowIndex: number;
    private currentPolicyNo: string = '';
    private el = inject(ElementRef);
    private documentService = inject(DocumentService);
    private loader = inject(LoadingService);
    private toastService = inject(ToastService);
    private dialog = inject(MatDialog);
    private renderer = inject(Renderer2);
    private iconRegistry = inject(MatIconRegistry);
    private currentDialogRef: any = null;
    private dialogSubscriptions = new Subscription();

    private readonly inboundSources = [
        'email',
        'iris',
        'psic',
        'upload',
        'allstate',
        'referral',
        'underwriting',
        'uw_3158',
        'concierge',
        'csr_7824',
        'wsc_4844',
        'pa_9680',
        'fax',
        'lgc',
        'paper mail',
        'wfappeal',
        'all_4401',
        'ui-p',
        'system',
    ];

    constructor(
        private store: Store<{ policy: PolicyState }>,
        private scrollStrategy: ScrollStrategyOptions,
        @Inject(APP_CONFIG) private config: AppConfig
    ) {}

    ngOnInit() {
        this.combinedSubscription = combineLatest([
            this.store.select((state) => state.policy.data).pipe(filter((data) => !!data)),
            this.store.select((state) => state.policy.policyNumber).pipe(filter((policyNumber) => !!policyNumber)),
        ]).subscribe(([policyData, policyNumber]) => {
            this.currentPolicyNo = policyNumber!;
            this.loadCommunications(policyData, policyNumber!);
        });

        this.cols = [
            { field: 'direction', header: 'Out/In', width: '70px' },
            { field: 'referenceId', header: 'ReferenceID', width: '310px' },
            { field: 'category', header: 'Category', width: '136px' },
            { field: 'type', header: 'Type', width: '70px' },
            { field: 'source', header: 'Source', width: '136px' },
            { field: 'status', header: 'Status', width: '100px' },
            { field: 'sendDate', header: 'Send date', width: '95px' },
            { field: 'receivedDate', header: 'Received date', width: '118px' },
        ];
    }

    private loadCommunications(policyData: any, policyKey: string) {
        // Check for inboundOutboundComms first, then fall back to documents
        const commsData = policyData[policyKey]?.inOutCommSnapshot || [];

        const mappedComms = commsData
            .map((d: any) => {
                // Handle both inboundOutboundComms and documents data structures
                const directionValue = d.commType?.toLowerCase() || '';
                const sourceValue = d.source?.toLowerCase() || '';

                return {
                    direction: directionValue
                        ? directionValue === 'inbound'
                            ? 'in'
                            : 'out'
                        : this.inboundSources.includes(sourceValue)
                        ? 'in'
                        : ('out' as 'in' | 'out'),
                    referenceId: d.messageId || '',
                    category: d.category || '',
                    type: d.type || '',
                    source: d.source || '',
                    sendDate: d.sentDate || '',
                    receivedDate: d.receivedDate || '',
                    status: d.status || '',
                    attachmentDocId: d.messageId,
                    taskId: d.taskId || '',
                };
            })
            .sort((a: Communication, b: Communication) => {
                const dateA = new Date(a.receivedDate || a.sendDate || 0).getTime();
                const dateB = new Date(b.receivedDate || b.sendDate || 0).getTime();
                return dateB - dateA;
            });

        this.allCommunications.set(mappedComms);
        this.filteredCommunications.set(mappedComms);
        this.communications.set(mappedComms);
        this.showAll.set(false);
        this.generateAggregates(mappedComms);
    }

    selectRow(index: number) {
        this.selectedRowIndex = index;
        this.viewDocument(this.communications()[index]);
    }

    viewDocument(comm: Communication) {
        if (comm.direction === 'in') {
            this.viewInboundDocument(comm);
        } else {
            this.viewOutboundDocument(comm);
        }
    }

    private isIrisTaskCategory(category: string): boolean {
        const normalized = category.trim().toLowerCase();
        return IRIS_TASK_CATEGORIES.some((c) =>
            normalized === c || normalized.startsWith(c)
        );
    }

    private viewInboundDocument(comm: Communication) {
        // These categories have no document — open the Iris task view directly
        if (this.isIrisTaskCategory(comm.category)) {
            if (!comm.taskId || !comm.referenceId) {
                this.toastService.add({
                    type: 'error',
                    title: '',
                    message: 'Task information is not available for this communication.',
                });
                return;
            }
            const url = `${this.config.irisBaseURL}/task-details?taskId=${encodeURIComponent(comm.taskId)}&messageId=${encodeURIComponent(comm.referenceId)}`;
            window.open(url, '_blank');
            return;
        }

        if (!comm.taskId) {
            this.toastService.add({
                type: 'error',
                title: '',
                message: 'Task ID is not available for this inbound communication.',
            });
            return;
        }

        this.loader.showLoading();
        this.documentService
            .getInboundTaskContent(comm.taskId)
            .pipe(finalize(() => this.loader.hideLoading()))
            .subscribe({
                next: (doclink: string) => {
                    this.openPdfDialog(doclink, comm.referenceId, comm.attachmentDocId);
                },
                error: () => {
                    this.toastService.add({ type: 'error', title: '', message: 'Error while opening the document.' });
                },
            });
    }

    private viewOutboundDocument(comm: Communication) {
        if (!comm.attachmentDocId) {
            this.toastService.add({
                type: 'error',
                title: '',
                message: 'Communication reference ID is not available.',
            });
            return;
        }

        const downloadRequest = {
            attachmentDocId: comm.attachmentDocId,
            downloadall: 'no',
            policyNo: this.currentPolicyNo,
            token: getAuthToken(),
            viewdocument: 'yes',
        };

        this.loader.showLoading();
        this.documentService
            .downloadDocument(downloadRequest)
            .pipe(finalize(() => this.loader.hideLoading()))
            .subscribe({
                next: (data: any) => {
                    if (data.statusCode === 200 && data.linkArr) {
                        const pdfUrl = this.documentService.getDocumentUrl(data.linkArr[0].link);
                        this.openPdfDialog(pdfUrl, comm.referenceId, comm.attachmentDocId);
                    } else {
                        this.toastService.add({
                            type: 'error',
                            title: '',
                            message: data.responseMessage || 'Error while opening the document.',
                        });
                    }
                },
                error: () => {
                    this.toastService.add({ type: 'error', title: '', message: 'Error while opening the document.' });
                },
            });
    }

    private openPdfDialog(pdfUrl: string, fileName: string, attachmentDocId?: string): void {
        if (this.currentDialogRef) {
            this.dialogSubscriptions.unsubscribe();
            this.dialogSubscriptions = new Subscription();
            this.currentDialogRef.close();
        }
        const uniqueClass = `pdf-dialog-${Math.random().toString(36).substring(2, 11)}`;

        const selectedComm = this.allCommunications().find((c) => c.attachmentDocId === attachmentDocId);

        this.currentDialogRef = this.dialog.open(PdfViewerComponent, {
            data: {
                pdfUrl,
                fileName,
                attachmentDocId,
                policyNo: this.currentPolicyNo,
                documentCategory: selectedComm?.category || '',
                attachmentDocType: selectedComm?.category || 'Others',
                subCategory: selectedComm?.category || '',
                commType: selectedComm?.direction,
            },
            width: '34%',
            height: '80%',
            panelClass: ['pdf-viewer-dialog', uniqueClass],
            maxWidth: '100vw',
            hasBackdrop: false,
            disableClose: false,
            scrollStrategy: this.scrollStrategy.noop(),
            position: { top: '5%', right: '5%' },
        });
        this.setupPinButton(uniqueClass, pdfUrl);
        this.setupDialogCleanup();
    }

    private setupPinButton(uniqueClass: string, pdfUrl: string): void {
        const openSub = this.currentDialogRef.afterOpened().subscribe(() => {
            const dialogElement = document.querySelector(`.${uniqueClass}`);
            if (!dialogElement?.querySelector('.external-pin-button')) {
                const pinButton = this.renderer.createElement('button');
                this.renderer.addClass(pinButton, 'external-pin-button');
                this.iconRegistry.getNamedSvgIcon('pin').pipe(take(1)).subscribe((svgElement) => {
                    this.renderer.appendChild(pinButton, svgElement);
                });
                this.renderer.listen(pinButton, 'click', () => {
                    window.open(pdfUrl, '_blank');
                    this.currentDialogRef.close();
                });
                this.renderer.appendChild(dialogElement, pinButton);
            }
        });
        this.dialogSubscriptions.add(openSub);
    }

    private setupDialogCleanup(): void {
        const closeSub = this.currentDialogRef.afterClosed().subscribe(() => {
            this.currentDialogRef = null;
            this.dialogSubscriptions.unsubscribe();
            this.dialogSubscriptions = new Subscription();
        });
        this.dialogSubscriptions.add(closeSub);
    }

    ngOnDestroy() {
        this.combinedSubscription?.unsubscribe();
        this.dialogSubscriptions.unsubscribe();
        this.currentDialogRef?.close();
    }

    loadMore() {
        this.showAll.set(true);
    }

    showLess() {
        this.showAll.set(false);
        this.el.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    filteredData(event: PillData[]) {
        this.filteredDataFilter = event;

        if (!event || event.length === 0) {
            this.filteredCommunications.set(this.allCommunications());
            this.communications.set(this.allCommunications());
            this.showAll.set(false);
            return;
        }

        const groupFieldMap: Record<string, keyof Communication> = {
            'Out/Inbound': 'direction',
            Category: 'category',
            Type: 'type',
            Source: 'source',
        };

        const activeFilters: Record<string, string[]> = {};
        event.forEach((pill: any) => {
            const pillData = pill.data ?? pill;
            const field = groupFieldMap[pillData.name];
            if (!field) return;
            const selectedKeys = (pillData.items || []).filter((item: any) => item.selected).map((item: any) => item.key);
            if (selectedKeys.length > 0) activeFilters[pillData.name] = selectedKeys;
        });

        const filtered = this.allCommunications().filter((comm) =>
            Object.entries(activeFilters).every(([group, keys]) => {
                const field = groupFieldMap[group];
                return keys.includes(comm[field] as string);
            })
        );

        this.filteredCommunications.set(filtered);
        this.communications.set(filtered);
        this.showAll.set(false);
    }

    filterReset() {
        this.filteredDataFilter = [];
        this.filteredCommunications.set(this.allCommunications());
        this.communications.set(this.allCommunications());
        this.showAll.set(false);
        this.generateAggregates(this.allCommunications());
    }

    generateAggregates(communicationsData: Communication[]) {
        const aggregatesObj: Record<string, Record<string, { key: string; doc_count: number; selected: boolean }>> = {
            Direction: {},
            Category: {},
            Type: {},
            Source: {},
        };

        communicationsData.forEach((comm) => {
            const fields: [string, string][] = [
                ['Direction', comm.direction],
                ['Category', comm.category],
                ['Type', comm.type],
                ['Source', comm.source],
            ];
            fields.forEach(([group, key]) => {
                if (!key) return;
                if (aggregatesObj[group][key]) {
                    aggregatesObj[group][key].doc_count++;
                } else {
                    aggregatesObj[group][key] = { key, doc_count: 1, selected: false };
                }
            });
        });

        const pillsObj: Record<string, any[]> = {};
        Object.keys(aggregatesObj).forEach((group) => {
            pillsObj[group] = Object.values(aggregatesObj[group]);
        });

        this.generatePillsData(pillsObj);
    }

    generatePillsData(aggregatesObj: Record<string, any[]>) {
        const newPills = Object.entries(aggregatesObj).map((ele: [string, any[]]) => ({
            data: {
                name: ele[0] === 'Direction' ? 'Out/Inbound' : ele[0],
                selectedItems: 0,
                isOpened: false,
                selectAll: false,
                isFiltered: false,
                items: ele[1].map((item: any) => ({ ...item, type: ele[0] })),
            },
        }));
        this.pills.set(newPills);
    }

    onClickHandler() {
        const currentPills = this.pills();
        if (currentPills.length > 0) {
            const updatedPills = [...currentPills];
            updatedPills[0].data.isOpened = !updatedPills[0].data.isOpened;
            this.pills.set(updatedPills);
        }
    }

    retryComm() {
        this.retry.emit();
    }
}
